/**
 * Created by Ning Jiang on 2021-06-18.
 */

import {WinPresentationController} from "../../WinPresentationController";
import {gsap, TimelineLite} from "gsap";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {Oktoberfest} from "../../../Oktoberfest";
import {OktoberfestGameConfig} from "../../../OktoberfestGameConfig";
import {BetWinMode} from "../../../../core/config/BetWinMode";
import {IBetLineWinData} from "../../../server/data/IOktoberfestData";
import {OktoberfestSymbolState} from "../../../symbol/OktoberfestSymbolConfig";
import {OktoberfestSymbol} from "../../../symbol/OktoberfestSymbol";
import {OktoberfestParsedGameData} from "../../../server/data/OktoberfestParsedGameData";
import {OktoberfestSound} from "../../../sound/OktoberfestSound";
import {IWinRatioConfig} from "../../../../core/config/WinRatio";
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";
import {GameHelper} from "../../../../core/utils/GameHelper";

export class InitialWinController extends WinPresentationController {

    private _reelSound = [true, true, true, true, true];
    private _isSkipped: boolean = false;

    private _mainTimeline: TimelineLite | null;

    constructor() {
        super("InitialWinController", true);
    }

    public static getEffectLevel(winRatio: number): number {
        for (let i = 1; i < 4; i++) {
            if (winRatio < OktoberfestGameConfig.WIN_RATIOS[i].ratio) {
                return i - 1;
            }
        }
        return 3;
    }

    private static playWinSound(currentWinRatio: IWinRatioConfig): void {
        let soundEffectName: string = OktoberfestSound.getNormalWinSoundName(currentWinRatio);
        if (soundEffectName != null) {
            OktoberfestSound.playSoundEffect(soundEffectName);
        }
    }

    protected hasWin(): boolean {
        this._isSkipped = false;
        return Oktoberfest.model.parsedGameData.betWins?.length > 0;
    }

    protected async createWinAnimation(): Promise<any> {
        const tl: TimelineLite = gsap.timeline();
        this._reelSound = [true, true, true, true, true];
        tl.add([
            this.getWinAnimation()
        ]);
        const isBigWin: boolean = Oktoberfest.model.parsedGameData.currentWinRatioConfig!.bigWinLevel > -1;
        const reels: string[][] = Oktoberfest.model.parsedGameData.reels;
        const isNextModeNormal: boolean = Oktoberfest.model.parsedGameData.nextMode === "NORMAL";
        if ((GameHelper.isReel2Stacked(reels) && (GameHelper.isReel4Stacked(reels))) && (!isBigWin) && (!isNextModeNormal)) {
            tl.add(() => {
                GameHelper.resetAllSymbols();
            }, tl.totalDuration());
        }
        this._mainTimeline = tl;
        await Promise.resolve(tl);
        this._mainTimeline = null;
    }

    protected getWinAnimation(): TimelineLite {
        const winRatio: number = Oktoberfest.model.parsedGameData.currentWinRatioConfig?.ratio!;
        const tl: TimelineLite = gsap.timeline();
        let winDelay: number = 0;
        tl.add(() => {
            OktoberfestSound.playSoundEffect(OktoberfestSound.SOUND_NAME_BETLINE_TURN_INTO_GOLD)
        }, winDelay);
        (<IBetLineWinData[]>Oktoberfest.model.parsedGameData.betWins).forEach((w: IBetLineWinData, index: number) => {
            if (OktoberfestGameConfig.BET_WIN_MODE === BetWinMode.BET_LINE) {
                tl.add(this.getSingleBetLineWinAnimation(<IBetLineWinData>w, index, winRatio), winDelay);
            }
        });
        Oktoberfest.background.onInitialWinPresentationStated(winRatio);

        if (Oktoberfest.model.parsedGameData.currentWinRatioConfig!.bigWinLevel > -1) {
            return tl; // no count up for big win.
        }
        const shouldRestrict = winRatio <= 1 && this.getBelowStakeWinRestriction();
        tl.add(this.getWinCountUpAnimation(shouldRestrict, winRatio));
        if (Oktoberfest.model.parsedGameData.mode === GameMode.FREESPIN) {
            Oktoberfest.fsMultiplier.onShowWin();
        }
        return tl;
    }

    protected getSingleBetLineWinAnimation(winData: IBetLineWinData, index: number, winRatio: number): TimelineLite {
        const tl: TimelineLite = gsap.timeline();
        let symbolDelay: number = 0.05;
        for (let betLineIndex = 0; betLineIndex < winData.nrOfSymbols; betLineIndex++) {
            tl.add(this.getSymbolWinAnimation(betLineIndex, winData.betLine[betLineIndex], index, winRatio), symbolDelay);
            symbolDelay += symbolDelay;
        }
        return tl;
    }

    protected getSymbolWinAnimation(reelId: number, symId: number, index: number, winRatio: number): TimelineLite {
        let sym: OktoberfestSymbol = Oktoberfest.reels[reelId].getSymbol(symId, false, false);
        const tl: TimelineLite = new TimelineLite();
        if (sym.symbolName !== "W1" && sym.symbolName !== "W2" && this._reelSound[reelId]) {
            this._reelSound[reelId] = false;
            tl.add(() => {
                if (!this._isSkipped) {
                    OktoberfestSound.playSoundEffect(OktoberfestSound.SOUND_NAME_GOLD_SYMBOL_LANDING);
                }
            }, 25 / 30);
        }
        if (sym.stackedSymbolName.isStacked) {
            if (sym.symbolName.indexOf("W") > -1) {
                let stackIndex: number = sym.stackedSymbolName.index;
                if (stackIndex !== 2) {
                    sym = Oktoberfest.reels[reelId].getSymbol(symId + (2 - stackIndex));
                }
            }
        }

        if (sym.symbolName !== "W3") {
            tl.add(() => {
                Oktoberfest.reels[reelId].reelView.addWinSymbol(sym);
            }, 0);
            tl.add(sym.getStateAnimation(OktoberfestSymbolState.WIN, {duration: 0.01, alpha: 1}, {
                duration: 0.01,
                alpha: 0
            }), 0);
        }
        if (sym.symbolName === "W3" && index === 0) {
            //to play only first tim
            tl.add(() => {
                Oktoberfest.reels[reelId].reelView.addWinSymbol(sym);
            }, 0);
            tl.add(sym.getStateAnimation(OktoberfestSymbolState.WIN, {duration: 0.01, alpha: 1}, {
                duration: 0.01,
                alpha: 0
            }), 0);
            tl.add(() => {
                OktoberfestSound.playSoundEffect(OktoberfestSound.SOUND_NAME_WILD_CHEERS);
            }, 25 / 30);
        }
        return tl;
    }

    protected getWinCountUpAnimation(shouldRestrict: boolean, winRatio: number): TimelineLite {
        const data: OktoberfestParsedGameData = Oktoberfest.model.parsedGameData;
        const tl: TimelineLite = gsap.timeline();
        if (data.currentWinRatioConfig!.bigWinLevel > -1) {
            return tl; // no count up for big win.
        }

        const duration: number = data.currentWinRatioConfig!.duration;
        if (!shouldRestrict) {
            const position = Oktoberfest.reels[2].getSymbol(1).getGlobalPosition();
            tl.add(() => {
                InitialWinController.playWinSound(data.currentWinRatioConfig!);
            }, duration);
            tl.add([Oktoberfest.initialCountUp.getCountUpAnimation(0, data.singleWin, duration, data.currentMultiplier), () => {
                Oktoberfest.winEffectView.playAnimation(position, InitialWinController.getEffectLevel(winRatio), shouldRestrict);
            }]);
        }
        tl.add(() => SlotGame.keypadPlugin.setWin(data.totalWin, data.showTotalWin));

        return tl;
    }

    protected skip(): void {
        this._isSkipped = true;
        if (this._mainTimeline!.isActive()) {
            this._mainTimeline?.progress(1);
            OktoberfestSound.playSoundEffect(OktoberfestSound.SOUND_NAME_GOLD_SYMBOL_LANDING);
        }

    }

    private getBelowStakeWinRestriction(): boolean {
        if (!Oktoberfest.apiPlugin.gameClientConfiguration.belowStakeWinRestriction) {
            return false;
        }
        const data: OktoberfestParsedGameData = Oktoberfest.model.parsedGameData;
        let playedBetValue: number = data.playedBetValue;
        return (data.singleWin / playedBetValue) <= 1;
    }
}
